'use client'
import Image from 'next/image'
import parse from 'html-react-parser'

// ─── Source platform icons + colors ──────────────────────────────────────────

const SOURCE_ICON = {
  google: (
    <svg width="13" height="13" viewBox="0 0 24 24">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
    </svg>
  ),
  facebook: (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="#1877F2">
      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
    </svg>
  ),
  airbnb: (
    <svg width="13" height="13" viewBox="0 0 32 32" fill="#FF5A5F">
      <path d="M16 1C7.163 1 0 8.163 0 17c0 8.836 7.163 16 16 16s16-7.164 16-16C32 8.163 24.837 1 16 1zm0 5c2.209 0 4 1.791 4 4s-1.791 4-4 4-4-1.791-4-4 1.791-4 4-4zm0 22c-4 0-7.333-2-9.333-5 .047-3.333 6-5.1 9.333-5.1 3.32 0 9.286 1.767 9.333 5.1C23.333 26 20 28 16 28z"/>
    </svg>
  ),
  tripadvisor: (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="#34E0A1">
      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 14.5v-9l6 4.5-6 4.5z"/>
    </svg>
  ),
  other: (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
    </svg>
  ),
}

const SOURCE_COLORS = {
  google: '#4285F4', facebook: '#1877F2', airbnb: '#FF5A5F', tripadvisor: '#34E0A1', other: '#6b7280',
}
const SOURCE_LABELS = {
  google: 'Google', facebook: 'Facebook', airbnb: 'Airbnb', tripadvisor: 'TripAdvisor', other: 'Other',
}

// ─── RTL helpers ──────────────────────────────────────────────────────────────
// Converts stored 'left'/'right' → logical 'start'/'end'
// textAlign:'start'/'end' respects dir="rtl" natively
const ALIGN_MAP      = { left: 'start', right: 'end', center: 'center', start: 'start', end: 'end' }
const normalizeAlign = (align) => ALIGN_MAP[align] ?? 'start'

// ─── Icons ────────────────────────────────────────────────────────────────────

const ICONS = {
  quote: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
      <path d="M3 21c3 0 7-1 7-8V5c0-1.25-.756-2.017-2-2H4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2 1 0 1 0 1 1v1c0 1-1 2-2 2s-1 .008-1 1.031V20c0 1 0 1 1 1z"/>
      <path d="M15 21c3 0 7-1 7-8V5c0-1.25-.757-2.017-2-2h-4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2h.75c0 2.25.25 4-2.75 4v3c0 1 0 1 1 1z"/>
    </svg>
  ),
  star:     <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>,
  user:     <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>,
  building: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="2" width="16" height="20" rx="2"/><path d="M9 22v-4h6v4"/><path d="M8 6h.01"/><path d="M16 6h.01"/><path d="M12 6h.01"/><path d="M12 10h.01"/><path d="M8 10h.01"/></svg>,
  calendar: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>,
  globe:    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>,
  none: null,
}

// ─── Star row ─────────────────────────────────────────────────────────────────

function StarRow({ rating, size, color }) {
  return (
    <span style={{ display: 'inline-flex', gap: 1, alignItems: 'center' }}>
      {[1,2,3,4,5].map(i => {
        const filled = rating >= i
        const half   = !filled && rating >= i - 0.5
        return (
          <svg key={i} width={size} height={size} viewBox="0 0 24 24">
            {filled ? (
              <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"
                fill={color} stroke="none" />
            ) : half ? (
              <>
                <defs><clipPath id={`rh${i}`}><rect x="0" y="0" width="12" height="24"/></clipPath></defs>
                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"
                  fill={color} clipPath={`url(#rh${i})`} />
                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"
                  fill="none" stroke={color} strokeWidth="2" />
              </>
            ) : (
              <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"
                fill="none" stroke={color} strokeWidth="2" />
            )}
          </svg>
        )
      })}
    </span>
  )
}

// ─── Source chip ──────────────────────────────────────────────────────────────

function SourceChip({ field, source }) {
  if (!field?.enabled) return null
  const color   = SOURCE_COLORS[source] ?? SOURCE_COLORS.other
  const label   = SOURCE_LABELS[source] ?? (source ? source.charAt(0).toUpperCase() + source.slice(1) : '')
  const icon    = SOURCE_ICON[source]   ?? SOURCE_ICON.other
  const variant = field.variant ?? 'icon+name'

  if (variant === 'icon-only') return <span style={{ display: 'inline-flex', alignItems: 'center' }}>{icon}</span>
  if (variant === 'badge') return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4,
      background: `${color}18`, color, border: `1px solid ${color}35`,
      fontSize: `${field.fontSize ?? 0.7}rem`, fontWeight: field.fontWeight ?? '500',
      padding: '2px 8px', borderRadius: field.badgeRadius ?? 20, lineHeight: 1.4 }}>
      {icon}{label}
    </span>
  )
  if (variant === 'text') return (
    <span style={{ fontSize: `${field.fontSize ?? 0.7}rem`, fontWeight: field.fontWeight ?? '500', color }}>{label}</span>
  )
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4,
      fontSize: `${field.fontSize ?? 0.7}rem`, fontWeight: field.fontWeight ?? '500', color }}>
      {icon}{label}
    </span>
  )
}

// ─── Format date ──────────────────────────────────────────────────────────────

function formatDate(date, fmt) {
  if (!date) return ''
  const d = new Date(date)
  if (fmt === 'relative') {
    const diff = Math.floor((Date.now() - d) / 86400000)
    if (diff < 7)   return `${diff} days ago`
    if (diff < 30)  return `${Math.floor(diff / 7)} weeks ago`
    if (diff < 365) return `${Math.floor(diff / 30)} months ago`
    return `${Math.floor(diff / 365)} years ago`
  }
  if (fmt === 'DD/MM/YYYY') return d.toLocaleDateString('en-GB')
  return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
}

// ─── TestamonialCard ──────────────────────────────────────────────────────────

const TestamonialCard = ({ testamonial, reviewCard, dictionary, lang }) => {
  if (!testamonial) return null

  const fields  = reviewCard?.fields ?? []
  const card    = reviewCard?.card   ?? {}
  const byId    = {}
  fields.forEach(f => { byId[f.id] = f })

  const f_avatar     = byId['avatar']     ?? { enabled: true,  size: 56, shape: 'circle', borderWidth: 2, borderColor: '#fff', fallbackBg: '#6b7280', fallbackColor: '#fff' }
  const f_name       = byId['name']       ?? { enabled: true,  fontSize: 0.9375, fontWeight: '700', color: '#111827', align: 'start', icon: 'none', bg: 'transparent' }
  const f_stars      = byId['stars']      ?? { enabled: true,  size: 14, color: '#f59e0b', showNumber: false, numberColor: '#6b7280', numberSize: 0.75 }
  const f_source     = byId['source']     ?? { enabled: true,  variant: 'icon+name', fontSize: 0.7, fontWeight: '500', badgeRadius: 20 }
  const f_content    = byId['content']    ?? { enabled: true,  fontSize: 0.8125, fontWeight: '400', color: '#374151', align: 'start', bg: 'transparent', lineClamp: 4, italic: true, showQuoteMark: true, quoteColor: 'var(--color-main)', quoteSize: 2.5, icon: 'none', fixedHeight: false }
  const f_date       = byId['date']       ?? { enabled: false, fontSize: 0.7,  fontWeight: '400', color: '#9ca3af', align: 'start', icon: 'calendar', bg: 'transparent', format: 'MMM YYYY' }
  const f_translated = byId['translated'] ?? { enabled: false, fontSize: 0.75, fontWeight: '400', color: '#9ca3af', align: 'start', bg: 'transparent', lineClamp: 3, italic: false, label2: 'Translated' }

  const bg          = card.bg             ?? '#ffffff'
  const radius      = card.radius         ?? 12
  const borderWidth = card.borderWidth    ?? 1
  const borderColor = card.borderColor    ?? '#e5e7eb'
  const shadow      = card.shadow         ?? '0 2px 12px rgba(0,0,0,0.07)'
  const padding     = card.contentPadding ?? 16
  const layout      = card.layout         ?? 'classic'
  const headerBgUse = card.headerBgUse    ?? false
  const headerBg    = card.headerBg       ?? '#f9fafb'

  const avatarRadius = f_avatar.shape === 'circle' ? '50%' : f_avatar.shape === 'rounded' ? '8px' : '4px'

  const cardStyle = {
    background: bg,
    borderRadius: radius,
    border: borderWidth > 0 ? `${borderWidth}px solid ${borderColor}` : 'none',
    boxShadow: shadow,
    overflow: 'hidden',
    fontFamily: "'Segoe UI', system-ui, sans-serif",
  }

  // ── Shared sub-elements ────────────────────────────────────────────────────

  const AvatarEl = () => !f_avatar.enabled ? null : (
    <div style={{
      width: f_avatar.size, height: f_avatar.size, flexShrink: 0,
      borderRadius: avatarRadius,
      border: `${f_avatar.borderWidth}px solid ${f_avatar.borderColor}`,
      overflow: 'hidden', position: 'relative',
      background: f_avatar.fallbackBg,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      {testamonial.cover
        ? <Image src={testamonial.cover} alt={testamonial.name ?? ''} fill style={{ objectFit: 'cover' }} />
        : <span style={{ fontSize: f_avatar.size * 0.35, fontWeight: 700, color: f_avatar.fallbackColor }}>
            {(testamonial.name ?? '?').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()}
          </span>
      }
    </div>
  )

  const HeaderEl = () => (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 12,
      padding: headerBgUse ? padding : 0,
      paddingBottom: headerBgUse ? 12 : 0,
      marginBottom: 10,
      background: headerBgUse ? headerBg : 'transparent',
      borderBottom: headerBgUse ? `1px solid ${borderColor}` : 'none',
    }}>
      <AvatarEl />
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
        {f_name.enabled && (
          <div style={{
            // normalizeAlign converts 'left'→'start', 'right'→'end'
            // textAlign:'start'/'end' respects dir="rtl"
            textAlign: normalizeAlign(f_name.align),
            background: f_name.bg !== 'transparent' ? f_name.bg : undefined,
            padding: f_name.bg !== 'transparent' ? '2px 6px' : 0,
            borderRadius: f_name.bg !== 'transparent' ? 4 : 0,
          }}>
            <span style={{ fontSize: `${f_name.fontSize}rem`, fontWeight: f_name.fontWeight, color: f_name.color }}>
              {f_name.icon !== 'none' && (
                // marginInlineEnd flips automatically with dir="rtl"
                <span style={{ opacity: 0.7, marginInlineEnd: 4 }}>{ICONS[f_name.icon]}</span>
              )}
              {testamonial.name}
            </span>
          </div>
        )}
        {f_stars.enabled && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <StarRow rating={parseFloat(testamonial.rating) || 0} size={f_stars.size} color={f_stars.color} />
            {f_stars.showNumber && (
              <span style={{ fontSize: `${f_stars.numberSize}rem`, color: f_stars.numberColor, fontWeight: 500 }}>
                {(parseFloat(testamonial.rating) || 0).toFixed(1)}
              </span>
            )}
          </div>
        )}
        {f_source.enabled && (
          <div style={{ marginTop: 1 }}>
            <SourceChip field={f_source} source={testamonial.source ?? 'other'} />
          </div>
        )}
      </div>
    </div>
  )

  const reviewText = lang !== 'en' && testamonial.translatedContent
    ? testamonial.translatedContent
    : testamonial.content

  const clamp  = f_content.lineClamp ?? 4
  const lineH  = 1.6
  const height = f_content.fixedHeight ? `${clamp * lineH * (f_content.fontSize ?? 0.8125)}rem` : undefined

  const ContentEl = () => !f_content.enabled ? null : (
    <div style={{
      background: f_content.bg !== 'transparent' ? f_content.bg : undefined,
      padding: f_content.bg !== 'transparent' ? '6px 8px' : 0,
      borderRadius: f_content.bg !== 'transparent' ? 6 : 0,
    }}>
      {f_content.showQuoteMark && (
        <span style={{
          fontSize: `${f_content.quoteSize ?? 2.5}rem`, lineHeight: 0.75,
          color: f_content.quoteColor, display: 'block',
          marginBottom: 4, fontFamily: 'Georgia, serif', opacity: 0.5,
        }}>"</span>
      )}
      {f_content.icon !== 'none' && (
        // marginInlineEnd flips automatically with dir="rtl"
        <span style={{ opacity: 0.6, marginInlineEnd: 6 }}>{ICONS[f_content.icon]}</span>
      )}
      <div style={{ textAlign: normalizeAlign(f_content.align), height, overflow: height ? 'hidden' : undefined }}>
        {reviewText ? (
          <span style={{
            display: '-webkit-box', WebkitLineClamp: clamp,
            WebkitBoxOrient: 'vertical', overflow: 'hidden',
            fontSize: `${f_content.fontSize}rem`, fontWeight: f_content.fontWeight,
            color: f_content.color, lineHeight: lineH,
            fontStyle: f_content.italic ? 'italic' : 'normal',
          }}>
            {parse(String(reviewText))}
          </span>
        ) : (
          <span style={{ fontSize: `${f_content.fontSize}rem`, color: f_content.color, fontStyle: 'italic', opacity: 0.6 }}>
            {dictionary?.section?.starReviewPre}{testamonial.rating}{dictionary?.section?.starReviewPost}
          </span>
        )}
      </div>
    </div>
  )

  const postHeaderOrder = fields.length > 0
    ? fields.filter(f => ['date','translated'].includes(f.id))
    : [f_date, f_translated]

  const renderPostField = (f) => {
    if (!f?.enabled) return null

    if (f.id === 'date') return (
      <div key="date" style={{
        textAlign: normalizeAlign(f.align),
        marginBottom: 4,
        background: f.bg !== 'transparent' ? f.bg : undefined,
        padding: f.bg !== 'transparent' ? '2px 6px' : 0,
        borderRadius: f.bg !== 'transparent' ? 4 : 0,
      }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5,
          fontSize: `${f.fontSize}rem`, fontWeight: f.fontWeight, color: f.color }}>
          {f.icon && f.icon !== 'none' && <span style={{ opacity: 0.7 }}>{ICONS[f.icon]}</span>}
          {formatDate(testamonial.reviewDate, f.format)}
        </span>
      </div>
    )

    if (f.id === 'translated') {
      if (!testamonial.translatedContent) return null
      return (
        <div key="translated" style={{
          borderTop: `1px solid ${borderColor}`, paddingTop: 8, marginTop: 4,
          background: f.bg !== 'transparent' ? f.bg : undefined,
          padding: f.bg !== 'transparent' ? '6px 8px' : undefined,
          borderRadius: f.bg !== 'transparent' ? 6 : 0,
        }}>
          <div style={{ fontSize: '0.65rem', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 3 }}>
            {f.label2 || 'Translated'}
          </div>
          <span style={{
            display: '-webkit-box', WebkitLineClamp: f.lineClamp ?? 3,
            WebkitBoxOrient: 'vertical', overflow: 'hidden',
            fontSize: `${f.fontSize}rem`, fontWeight: f.fontWeight,
            color: f.color, lineHeight: 1.5,
            fontStyle: f.italic ? 'italic' : 'normal',
            textAlign: normalizeAlign(f.align),
          }}>
            {testamonial.translatedContent}
          </span>
        </div>
      )
    }
  }

  // ── Layout: classic ───────────────────────────────────────────────────────

  if (layout === 'classic') return (
    <div style={cardStyle}>
      <div style={{ padding }}>
        <HeaderEl />
        <ContentEl />
        {postHeaderOrder.map(f => renderPostField(f))}
      </div>
    </div>
  )

  // ── Layout: minimal ───────────────────────────────────────────────────────

  if (layout === 'minimal') return (
    <div style={{ ...cardStyle, padding }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        {f_stars.enabled && (
          <StarRow rating={parseFloat(testamonial.rating) || 0} size={f_stars.size} color={f_stars.color} />
        )}
        {f_source.enabled && <SourceChip field={f_source} source={testamonial.source ?? 'other'} />}
      </div>
      <ContentEl />
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 12 }}>
        <AvatarEl />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {f_name.enabled && (
            <span style={{ fontSize: `${f_name.fontSize}rem`, fontWeight: f_name.fontWeight, color: f_name.color }}>
              {testamonial.name}
            </span>
          )}
          {postHeaderOrder.map(f => renderPostField(f))}
        </div>
      </div>
    </div>
  )

  // ── Layout: bubble ────────────────────────────────────────────────────────
  // borderStartStartRadius / borderEndStartRadius are the logical equivalents
  // of borderTopLeftRadius / borderBottomLeftRadius — they flip in RTL so the
  // speech-bubble "tail" always points toward the avatar below.

  return (
    <div style={{ fontFamily: "'Segoe UI', system-ui, sans-serif" }}>
      <div style={{
        background: bg, borderRadius: radius,
        // borderEndStartRadius replaces borderBottomLeftRadius so the bubble
        // tail appears on the correct side in RTL
        borderEndStartRadius: 4,
        border: borderWidth > 0 ? `${borderWidth}px solid ${borderColor}` : 'none',
        boxShadow: shadow, padding, marginBottom: 12, overflow: 'hidden',
      }}>
        {f_stars.enabled && (
          <div style={{ marginBottom: 8 }}>
            <StarRow rating={parseFloat(testamonial.rating) || 0} size={f_stars.size} color={f_stars.color} />
          </div>
        )}
        <ContentEl />
        {postHeaderOrder.filter(f => ['date','translated'].includes(f.id)).map(f => renderPostField(f))}
      </div>
      {/* paddingInlineStart replaces paddingLeft so the avatar indent
          appears at the visual start edge in both LTR and RTL */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, paddingInlineStart: 8 }}>
        <AvatarEl />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {f_name.enabled && (
            <span style={{ fontSize: `${f_name.fontSize}rem`, fontWeight: f_name.fontWeight, color: f_name.color }}>
              {testamonial.name}
            </span>
          )}
          {f_source.enabled && <SourceChip field={f_source} source={testamonial.source ?? 'other'} />}
        </div>
      </div>
    </div>
  )
}

export default TestamonialCard